import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { MapPin, Users, MessageCircle, Edit2, Save, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

const tempColors = { hot: '#ef4444', warm: '#f59e0b', cold: '#3b82f6', frozen: '#94a3b8' };
const tempLabels = { hot: '🔥 Quente', warm: '🌡️ Morno', cold: '❄️ Frio', frozen: '🧊 Congelado' };
const tempEmoji = { hot: '🔥', warm: '🌡️', cold: '❄️', frozen: '🧊' };

const GroupHeatmap = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [loadingEngagement, setLoadingEngagement] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [clusterFilter, setClusterFilter] = useState(null); // IDs dos grupos no cluster clicado

  const clienteId = user?.cliente_id;

  useEffect(() => { if (clienteId) loadHeatmap(); }, [clienteId, days]);

  // Init map once
  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return;
    const map = L.map(mapRef.current).setView([-14.24, -51.93], 4);
    map.getContainer().style.background = darkMode ? '#1a1a2e' : '#f0f0f0';
    const style = document.createElement('style');
    // Dark mode sem provider pago: inverte os tiles do OSM via CSS (OSM não tem
    // basemap escuro oficial sem key). No modo claro, tiles normais.
    style.textContent =
      '.leaflet-tile{outline:none!important;border:none!important}' +
      (darkMode
        ? '.leaflet-tile-pane{filter:invert(1) hue-rotate(180deg) brightness(0.95) contrast(0.9)}'
        : '');
    map.getContainer().appendChild(style);
    // OpenStreetMap padrão: gratuito e SEM API key. O CARTO (basemaps.cartocdn.com)
    // passou a exigir api key ("API KEY REQUIRED"), por isso o fundo do mapa não
    // carregava mais.
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapInstance.current = map;
    setMapReady(true);
    return () => { map.remove(); mapInstance.current = null; setMapReady(false); };
  }, []);

  // Draw heat + markers whenever groups or map changes
  const drawMap = useCallback(() => {
    const map = mapInstance.current;
    if (!map) return;

    // Clear everything
    map.eachLayer(layer => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer);
    });

    const withCoords = groups.filter(g => g.latitude && g.longitude);
    if (withCoords.length === 0) return;

    // Heat layer — intensity based on score
    const pts = withCoords.map(g => [parseFloat(g.latitude), parseFloat(g.longitude), (g.score || 0) / 100]);
    L.heatLayer(pts, {
      radius: 40, blur: 30, maxZoom: 17, max: 1.0, minOpacity: 0.3,
      gradient: { 0.0: '#3b82f6', 0.25: '#06b6d4', 0.5: '#f59e0b', 0.75: '#f97316', 1.0: '#ef4444' },
    }).addTo(map);

    // Cluster markers
    const clusterGroup = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster) => {
        const childMarkers = cluster.getAllChildMarkers();
        const count = childMarkers.length;
        const avgScore = childMarkers.reduce((sum, m) => sum + (m.options.score || 0), 0) / count;
        const color = avgScore >= 60 ? '#ef4444' : avgScore >= 40 ? '#f59e0b' : avgScore >= 20 ? '#3b82f6' : '#94a3b8';
        return L.divIcon({
          html: `<div style="background:${color};color:#fff;border-radius:50%;width:36px;height:36px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)">${count}</div>`,
          className: '',
          iconSize: [36, 36],
        });
      },
    });

    clusterGroup.on('clusterclick', (e) => {
      const childMarkers = e.layer.getAllChildMarkers();
      const ids = childMarkers.map(m => m.options.groupId);
      setClusterFilter(ids);
      setSelectedGroup(null);
    });

    withCoords.forEach(g => {
      const marker = L.circleMarker([parseFloat(g.latitude), parseFloat(g.longitude)], {
        radius: Math.max(6, Math.min(18, (g.score || 0) / 5)),
        fillColor: tempColors[g.temperature], color: '#fff', weight: 2, fillOpacity: 0.9,
        groupId: g.id,
        score: g.score || 0,
      }).bindPopup(`
        <div style="min-width:180px;font-family:system-ui">
          <b style="font-size:14px">${tempEmoji[g.temperature]} ${g.nome}</b><br/>
          <span style="color:#666">${g.bairro ? g.bairro + ', ' : ''}${g.cidade || ''}</span>
          <hr style="margin:6px 0;border-color:#eee"/>
          <div style="font-size:12px;line-height:1.8">
            👥 ${g.total_participantes} participantes<br/>
            💬 ${g.mensagens} mensagens (${days}d)<br/>
            🙋 ${g.participantes_ativos} ativos<br/>
            <b style="color:${tempColors[g.temperature]}">${g.engagement_rate}% engajamento</b>
          </div>
        </div>
      `);
      marker.on('click', () => {
        setClusterFilter(null);
        loadEngagement(g);
      });
      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);

    // Fit — center between all points
    if (withCoords.length === 1) {
      map.setView([parseFloat(withCoords[0].latitude), parseFloat(withCoords[0].longitude)], 13);
    } else {
      const bounds = L.latLngBounds(withCoords.map(g => [parseFloat(g.latitude), parseFloat(g.longitude)]));
      map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });
    }
  }, [groups, days, setClusterFilter, setSelectedGroup]);

  useEffect(() => { if (mapReady) drawMap(); }, [mapReady, drawMap]);

  const loadHeatmap = async () => {
    setLoading(true);
    try {
      const res = await api.get('/grupos-whatsapp/heatmap/data', { params: { cliente_id: clienteId, days } });
      setGroups(res.data || []);
    } catch { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  };

  const loadEngagement = async (group) => {
    setSelectedGroup(group);
    setLoadingEngagement(true);
    try {
      const res = await api.get(`/grupos-whatsapp/${group.id}/engagement`, { params: { cliente_id: clienteId, days } });
      setEngagement(res.data);
    } catch { toast.error('Erro ao carregar engajamento'); }
    finally { setLoadingEngagement(false); }
  };

  const saveLocation = async () => {
    try {
      await api.patch(`/grupos-whatsapp/${editingGroup.id}/location`, editForm);
      toast.success('Localização salva!');
      setEditingGroup(null);
      loadHeatmap();
    } catch { toast.error('Erro ao salvar'); }
  };

  const startEdit = (group) => {
    setEditingGroup(group);
    setEditForm({
      cidade: group.cidade || '', bairro: group.bairro || '', estado: group.estado || '',
      latitude: group.latitude || '', longitude: group.longitude || '', tags: group.tags || [],
    });
  };

  const geocode = async () => {
    const q = `${editForm.bairro ? editForm.bairro + ', ' : ''}${editForm.cidade || ''}, ${editForm.estado || 'Brasil'}`;
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`);
      const data = await res.json();
      if (data[0]) {
        setEditForm(f => ({ ...f, latitude: parseFloat(data[0].lat), longitude: parseFloat(data[0].lon) }));
        toast.success('Coordenadas encontradas!');
      } else toast.warning('Localização não encontrada');
    } catch { toast.error('Erro ao geocodificar'); }
  };

  const card = `rounded-xl p-4 ${darkMode ? 'bg-zinc-800 border border-zinc-700' : 'bg-white border border-gray-200'}`;
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full rounded-lg py-2 px-3 text-sm border focus:outline-none focus:ring-1 focus:ring-nomoz-orange ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  const stats = {
    hot: groups.filter(g => g.temperature === 'hot').length,
    warm: groups.filter(g => g.temperature === 'warm').length,
    cold: groups.filter(g => g.temperature === 'cold').length,
    frozen: groups.filter(g => g.temperature === 'frozen').length,
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(stats).map(([temp, count]) => (
          <div key={temp} className={card}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${textP}`}>{count}</p>
                <p className={`text-xs ${textS}`}>{tempLabels[temp]}</p>
              </div>
              <div className="text-2xl">{tempEmoji[temp]}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Período */}
      <div className="flex items-center gap-3">
        <span className={`text-sm font-medium ${textS}`}>Período:</span>
        {[7, 14, 30].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${days === d ? 'bg-nomoz-orange text-white' : darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
            {d} dias
          </button>
        ))}
      </div>

      {/* Legenda */}
      <div className={`${card} flex items-center gap-4`}>
        <span className={`text-sm font-medium ${textS}`}>Legenda:</span>
        <div className="flex items-center gap-1 flex-1">
          <span className="text-xs">❄️ Frio</span>
          <div className="flex-1 h-3 rounded-full" style={{ background: 'linear-gradient(to right, #3b82f6, #06b6d4, #f59e0b, #f97316, #ef4444)' }} />
          <span className="text-xs">🔥 Quente</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Mapa */}
        <div className={`lg:col-span-2 ${card}`}>
          <h3 className={`text-lg font-bold mb-3 ${textP}`}><MapPin className="inline h-5 w-5 mr-2" />Mapa de Temperatura</h3>
          {groups.filter(g => g.latitude && g.longitude).length === 0 && !loading && (
            <div className={`text-center py-3 mb-3 rounded-lg ${darkMode ? 'bg-zinc-900/50' : 'bg-yellow-50'}`}>
              <p className={`text-sm ${textS}`}>⚠️ Configure a localização dos grupos clicando no ✏️</p>
            </div>
          )}
          {loading && (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nomoz-orange" /></div>
          )}
          <div ref={mapRef} style={{ height: 500, width: '100%', borderRadius: 8 }} />
        </div>

        {/* Lista + engajamento */}
        <div className="space-y-3 max-h-[650px] overflow-y-auto pr-1">
          {!selectedGroup ? (
            <>
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-bold ${textP}`}>
                  {clusterFilter ? `Grupo no ponto (${clusterFilter.length})` : `Grupos (${groups.length})`}
                </h3>
                {clusterFilter && (
                  <button onClick={() => setClusterFilter(null)} className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    Mostrar todos
                  </button>
                )}
              </div>
              {(clusterFilter ? groups.filter(g => clusterFilter.includes(g.id)) : groups).sort((a, b) => b.engagement_rate - a.engagement_rate).map(g => (
                <div key={g.id} className={`${card} cursor-pointer hover:ring-2 hover:ring-nomoz-orange transition-all`}
                  onClick={() => loadEngagement(g)}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{tempEmoji[g.temperature]}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${textP}`}>{g.nome}</p>
                      <p className={`text-xs ${textS}`}>{g.bairro || g.cidade || 'Sem local'}</p>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-xs ${textS}`}>👥{g.total_participantes}</span>
                        <span className={`text-xs ${textS}`}>💬{g.mensagens}</span>
                        <span className="text-xs font-bold" style={{ color: tempColors[g.temperature] }}>{g.engagement_rate}%</span>
                      </div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); startEdit(g); }}
                      className={`p-1.5 rounded-lg flex-shrink-0 ${darkMode ? 'hover:bg-zinc-600' : 'hover:bg-gray-200'}`}>
                      <Edit2 className={`h-3.5 w-3.5 ${textS}`} />
                    </button>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className={card}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className={`font-bold ${textP}`}>{tempEmoji[selectedGroup.temperature]} {selectedGroup.nome}</h3>
                  <p className={`text-xs ${textS}`}>{selectedGroup.cidade ? `📍 ${selectedGroup.bairro ? selectedGroup.bairro + ', ' : ''}${selectedGroup.cidade}` : ''} • {days}d</p>
                </div>
                <button onClick={() => { setSelectedGroup(null); setEngagement(null); }}>
                  <X className={`h-4 w-4 ${textS}`} />
                </button>
              </div>
              {loadingEngagement ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nomoz-orange" /></div>
              ) : engagement?.participants?.length === 0 ? (
                <div className={`text-center py-8 ${textS}`}>
                  <MessageCircle className="mx-auto h-10 w-10 mb-2 opacity-50" />
                  <p className="text-sm">Nenhuma mensagem no período</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {engagement?.participants?.map((p, i) => (
                    <div key={i} className={`p-3 rounded-lg ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <p className={`text-sm font-medium truncate ${textP}`}>{p.name || p.phone}</p>
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: tempColors[p.temperature] + '20', color: tempColors[p.temperature] }}>
                          {tempEmoji[p.temperature]}
                        </span>
                      </div>
                      <div className="flex gap-3">
                        <span className={`text-xs ${textS}`}>💬 {p.messages}</span>
                        <span className={`text-xs ${textS}`}>
                          {p.days_since_last === 0 ? '🟢 Hoje' : p.days_since_last === 1 ? '🟡 Ontem' : `⚪ ${p.days_since_last}d`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal editar localização */}
      {editingGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={() => setEditingGroup(null)}>
          <div className={`rounded-xl shadow-xl max-w-md w-full p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-bold ${textP}`}>📍 {editingGroup.nome}</h3>
              <button onClick={() => setEditingGroup(null)}><X className={`h-5 w-5 ${textS}`} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Cidade</label>
                  <input className={inputCls} value={editForm.cidade} onChange={e => setEditForm(f => ({ ...f, cidade: e.target.value }))} placeholder="Campinas" />
                </div>
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Estado</label>
                  <input className={inputCls} value={editForm.estado} onChange={e => setEditForm(f => ({ ...f, estado: e.target.value }))} placeholder="SP" />
                </div>
              </div>
              <div>
                <label className={`text-xs font-medium ${textS}`}>Bairro</label>
                <input className={inputCls} value={editForm.bairro} onChange={e => setEditForm(f => ({ ...f, bairro: e.target.value }))} placeholder="Cambuí" />
              </div>
              <button onClick={geocode} className="w-full px-3 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600">
                🔍 Buscar coordenadas automaticamente
              </button>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Latitude</label>
                  <input className={inputCls} type="number" step="any" value={editForm.latitude} onChange={e => setEditForm(f => ({ ...f, latitude: e.target.value }))} />
                </div>
                <div>
                  <label className={`text-xs font-medium ${textS}`}>Longitude</label>
                  <input className={inputCls} type="number" step="any" value={editForm.longitude} onChange={e => setEditForm(f => ({ ...f, longitude: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={`text-xs font-medium ${textS}`}>Tags (separadas por vírgula)</label>
                <input className={inputCls} value={(editForm.tags || []).join(', ')}
                  onChange={e => setEditForm(f => ({ ...f, tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
                  placeholder="leads, vip, campinas" />
              </div>
              <button onClick={saveLocation} className="w-full px-4 py-2 bg-nomoz-orange text-white rounded-lg font-medium hover:bg-nomoz-orange-dark">
                <Save className="inline h-4 w-4 mr-2" />Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupHeatmap;
